// @ts-nocheck
// Agent Portal (/cti2) — redesigned agent CTI shell, previewable
// independently of the production /dashboard route.
//
// Orchestrator adapted from src/features/cti/CTIAgentUI.tsx: reuses the
// SAME call-handling engine (useCallStore, the SIP softphone bootstrap,
// the disposition lifecycle, hold/mute/transfer/conference handlers, call
// record persistence) and the SAME reused screens (RingingScreen,
// DispositionScreen, PendingDispositionsPanel, CTIRightRail,
// TransferConferencePicker) with their EXACT prop contracts — only the
// shell chrome (Topbar/Dialer) and the screen-composition are new.
//
// screen === 'oncall' does NOT render OnCallScreen.tsx — it inlines
// CustomerProfileCard/CallScriptCard directly (see that branch below),
// matching how production's CTIAgentUI.tsx/IdleScreen.tsx already show a
// live call, and the cti-signal mock's live-call layout.
//
// "Live call" (the default view, driven by `screen`) shows only a simple
// idle placeholder while `screen === 'idle'` — Call Stats / Voicemails /
// Leads / Follow-Ups live in "My dashboard" (AgentDashboardView) instead,
// a separate, always-available view toggled from the Topbar, not tied to
// call state. `views/IdleView.tsx` (the tab-switching version of the same
// panels) is unused today; kept in the tree per explicit instruction to
// fold it in differently later, not deleted.
//
// Deliberate trims vs CTIAgentUI.tsx (not needed by this shell's design):
//   - No announcements/campaign-groups/schedule/performance panels, no
//     DashboardLayoutEditor, no AgentReportsPanel/AgentRealtimeStats.
//     Campaign/agent-group data IS still fetched, because CallScriptCard
//     needs it.
// Everything that touches real call state, disposition, or persistence
// is ported byte-faithful.
//
// Always portals to document.body (unlike CTIAgentUI.tsx's toggleable
// focusMode) — this shell has no non-fullscreen state, so the AppShell's
// own sidebar/topbar never shows through underneath it.
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CTIRightRail from '@/features/cti/components/CTIRightRail';
import TransferConferencePicker from '@/features/cti/components/TransferConferencePicker';
import hangupAudio from '@/features/cti/screens/hangup-audio.mp3';
// Replaces OnCallScreen/RingingScreen/DispositionScreen as the "Live call"
// view for every non-pending screen state — the same always-mounted,
// self-gating inline-cards pattern production's CTIAgentUI.tsx/
// IdleScreen.tsx already use (CustomerProfileCard/CallScriptCard/
// DispositionCard decide their own visibility from useCallStore; the
// parent never branches on `screen` to show/hide them), and the shape the
// cti-signal mock's live-call layout draws.
import LiveCallStack from './components/LiveCallStack';
import {
  fetchNextPendingLeadInGroup,
  saveNextLeadDialIntent,
  clearNextLeadDialIntent,
  takeNextLeadDialIntent,
} from './hooks/useLeadAutoAdvance';
import AgentBreakPanel from './components/AgentBreakPanel';
import BreakScreen from './components/BreakScreen';
import NotificationDrawer from './components/NotificationDrawer';
import CallQualityFeedbackTray from '@/features/call-quality-feedback/CallQualityFeedbackTray';
import Topbar from './components/Topbar';
import Dialer from './components/Dialer';
import { useCustomerHistoryCache } from './hooks/useCustomerHistoryCache';
import { useCallActions } from './hooks/useCallActions';
import { useIdleTracking } from './hooks/useIdleTracking';
import { useScreenState } from './hooks/useScreenState';
import { useDispositionFlow } from './hooks/useDispositionFlow';
import { sanitizeDialInput, mergeSameCallRows, playNotificationTone, watchNotifications, fetchAssignedNotifications } from './utils/dialerFormat';
import AgentDashboardView from './views/AgentDashboardView';
import TeamDashboardView from './views/TeamDashboardView';
import { isTeamLeadUser } from './acl';
import SideDrawer from './components/SideDrawer';
import UserProfilePage from '@/features/users/UserProfilePage';
// Realtime socket — same hooks DashboardPage.tsx calls at the page level
// for CTIAgentUI (comment there: "Kept at page level ... to match legacy
// DashboardPage lifecycle"). This route bypasses DashboardPage, so without
// calling them here the socket.io connection never opens.
import useSocket from '@/hooks/useSocket';
import { useSingleTabGuard } from '@/hooks/useSingleTabGuard';
import useMobileCallSocket from '@/hooks/useMobileCallSocket';
import ExitGuard from '@/components/ExitGuard';
import ReloadGuardDialog from './components/ReloadGuardDialog';
// The reused screens/components above (RingingScreen/DispositionScreen/
// CTIRightRail/TransferConferencePicker/CustomerProfileCard/CallScriptCard/
// CallQualityIndicator) are styled entirely by cti.css's ctiv3-*/etc.
// classes — it's normally pulled in via CTIAgentUI.tsx, which this route
// doesn't render, so it has to be imported here directly or those screens
// render unstyled.
import '../cti/cti.css';
import './cti2.css';

import { postApiCall, patchApiCall, getApiCall } from '@/service/httpCall';
import { getContactGroupMemberByNumber } from '@/service/contactGroupMember';
import { trackActivity } from '@/service/activityTracker';
import { Storage, restoreActiveProfileFromSession } from '@/utilities/setting';
import {
  API_AGENT_DASHBOARD_CALLS,
  API_CALLS_CREATE,
  API_DISPOSITION_FOLLOW_UP_COMPLETE_BY_CALL,
  API_DISPOSITION_FOLLOW_UP_DIAL_NUMBER,
  API_AGENT_CALL_LOG_DIAL_NUMBER,
  API_VOICE_CAMPAIGNS_GET_ONE,
  API_CALL_SCRIPT_BY_CONTEXT,
  API_REMARK_STATUS_LIST,
  API_ALL_CONTACTS_LIST,
  API_AGENT_MY_ASSIGNED_CLIS,
  API_AGENT_CLI_FALLBACK,
  API_SINGLE_AGENT_GROUP,
  API_AGENT_LEADS_COMPLETE,
  API_AGENT_DASHBOARD_CAMPAIGNS,
  API_AGENT_ANNOUNCEMENT_LIST,
  API_NOTIFICATION_MARK_READ,
  API_NOTIFICATION_MARK_ALL_READ,
} from '@/service/url';
import { deriveCallTypeFlags, getActiveTelephonyProfile } from '@/utilities/mobileCallEvents';
import { getAgentMasking } from '@/utilities/cliDisplay';
import { callRowCustomerNumber } from '@/features/cti/ctiHelpers';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { BreakContext } from '@/router/context/BreakContext';
import { useCallStore, isWrapUpHoldActive, WRAP_UP_HOLD_DIAL_MESSAGE } from '@/router/zustand/callStore';
import WrapUpHoldBanner from './components/WrapUpHoldBanner';
import { useTranscriptStore } from '@/router/zustand/transcriptStore';
import { useThemeStore } from '@/lib/theme/store';
import { applySpeakerToAll, followSpeakerChanges } from './audio/audioDevices';
import { AFTER_CALL_REFETCH_MS } from '@/features/cti/components/call-stats/useCallStatsData';
import { useDashboardRefresh } from './useDashboardRefresh';

const AGENT_CALLS_STALE_MS = 60000;

/** Breathing room between disposing one lead and auto-dialling the next. */
const AUTO_DIAL_NEXT_LEAD_DELAY_MS = 3000;
/** After a refresh, how long to let break/wrap-up/SIP state land first. */
const AUTO_DIAL_RESUME_SETTLE_MS = 5000;
/** …and how much longer to keep waiting on the softphone to register. */
const AUTO_DIAL_RESUME_PHONE_WAIT_MS = 25000;

/** Only outgoing/both (or legacy unset) numbers may be used as outbound CLI. */
function filterOutboundClis(list) {
  return (Array.isArray(list) ? list : []).filter((cli) => {
    const routingType = String(cli?.number_routing_type || '').trim().toLowerCase();
    if (!routingType) return true;
    return routingType === 'outgoing' || routingType === 'both';
  });
}

const setLocalAudioEnabled = (session, enabled) => {
  try {
    const handler = session?.sessionDescriptionHandler;
    const pc = handler?.peerConnection || handler?._peerConnection || session?.connection || session?.peerConnection;
    if (!pc || typeof pc.getSenders !== 'function') return false;
    let applied = false;
    pc.getSenders().forEach((sender) => {
      if (sender?.track && sender.track.kind === 'audio') {
        sender.track.enabled = Boolean(enabled);
        applied = true;
      }
    });
    return applied;
  } catch (e) {
    console.error('setLocalAudioEnabled failed:', e);
    return false;
  }
};

// My Dashboard's side drawer content — the panel, its title, and the
// Topbar toggle all render only when this is non-null, so the layout seam
// and the reusable SideDrawer are in place without an empty drawer shipping
// to agents. To turn it on: `{ title: 'Something', content: <Something /> }`.
const DASHBOARD_DRAWER: { title: string; content: React.ReactNode } | null = null;

export default function AgentPortalPage() {
  // Was a `useState` initializer reading `document.documentElement`'s class
  // once at mount — froze at whatever the class happened to be at that
  // exact instant and never updated again, so e.g. Performance-panel colors
  // (isDark derived from this) could stay light-themed forever even after
  // the rest of the chrome (driven live by the theme store) switched to
  // dark. Reading the store directly keeps it in sync, matching Topbar.tsx.
  const themeMode = useThemeStore((s) => s.mode);
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  const theme = themeMode === 'dark' || (themeMode === 'system' && systemPrefersDark) ? 'dark' : 'light';
  const [userData, setUserData] = useState<any>(null);
  const [loggedUserId, setLoggedUserId] = useState<any>(null);

  // Same hooks/singleton DashboardPage.tsx calls for CTIAgentUI — reused
  // as-is, not reimplemented. No isAgent gate needed (this page only ever
  // renders for an agent); both hooks no-op internally on a falsy agentId.
  const tabBlocked = useSingleTabGuard(loggedUserId ?? null);
  const socketAgentId = tabBlocked ? null : loggedUserId ?? null;
  useSocket(socketAgentId);
  useMobileCallSocket(socketAgentId, userData?.agentData?.agent_number ?? null);

  const [telephonyProfileActive, setTelephonyProfileActive] = useState(false);
  const [dialNum, setDialNum] = useState('');
  const dialNumRef = useRef(dialNum);
  const dialerLockedPrefixRef = useRef('');
  const dialerPrevValueRef = useRef('');
  // demo-1.ts calls document.getElementById('hangupAudio') on every hangup
  // — CTIAgentUI.tsx (the legacy /cti shell) mounts that element, /cti2
  // never did, so the sound silently no-op'd here ("Hangup audio element
  // not found with id 'hangupAudio'" in console). Same element + the same
  // one-time "unlock" play/pause CTIAgentUI.tsx uses to satisfy browsers'
  // autoplay policy before the first real hangup ever needs it.
  const hangupAudioRef = useRef<any>(null);
  useEffect(() => {
    const unlockAudio = () => {
      const audio = hangupAudioRef.current;
      if (!audio) return;
      audio.play().then(() => { audio.pause(); audio.currentTime = 0; }).catch(() => {});
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };
    document.addEventListener('click', unlockAudio, { once: true });
    document.addEventListener('touchstart', unlockAudio, { once: true });
    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };
  }, []);
  const [manualScreen, setManualScreen] = useState<any>(null);

  // Only what the BreakScreen takeover below needs — the rail's break flow
  // (AgentBreakPanel.tsx) reads BreakContext itself, and the break
  // catalogue is fetched by BreakProvider on mount, so nothing here
  // duplicates that any more.
  const [breakActionLoading, setBreakActionLoading] = useState(false);
  const { breakEndTime, userStatus, stopBreak, countdown, breakExceeded, activeBreak } =
    (useContext(BreakContext) as any) || {};

  const [agentCallsData, setAgentCallsData] = useState<any>({});
  const [assignedClis, setAssignedClis] = useState<any[]>([]);
  const [loadingAssignedClis, setLoadingAssignedClis] = useState(false);
  const [cliSource, setCliSource] = useState<any>(null);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [remarkStatusList, setRemarkStatusList] = useState<any[]>([]);
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notifDrawerOpen, setNotifDrawerOpen] = useState(false);

  const [activeCampaignFromSip, setActiveCampaignFromSip] = useState<any>(null);
  const [activeCampaignLoading, setActiveCampaignLoading] = useState(false);
  const [activeAgentGroupFromSip, setActiveAgentGroupFromSip] = useState<any>(null);
  const [activeAgentGroupLoading, setActiveAgentGroupLoading] = useState(false);
  const [contextScript, setContextScript] = useState<any>(null);
  const [contextScriptSource, setContextScriptSource] = useState<any>(null);
  const [contextScriptLoading, setContextScriptLoading] = useState(false);

  const lastSipCampaignIdRef = useRef<any>(null);
  const lastSipCallGroupIdRef = useRef<any>(null);
  const lastCallerContactRef = useRef<any>(null);
  const lastSipLeadDataRef = useRef<any>(null);
  const contextScriptFetchedKeyRef = useRef<any>(null);
  const autoCompletedLeadRef = useRef<any>(null);
  const autoDialNextLeadTimerRef = useRef<any>(null);
  const nextLeadIntentCheckedRef = useRef(false);
  const userStatusRef = useRef<any>(null);

  useEffect(() => { dialNumRef.current = dialNum; }, [dialNum]);
  useEffect(() => { userStatusRef.current = userStatus; }, [userStatus]);

  const webrtcStatus = useCallStore((s) => s.webrtcPhoneStatus);
  const webrtcIdleTime = useCallStore((s) => s.webrtcPhoneStartTime);
  const idleSessionStartedAt = useCallStore((s) => s.idleSessionStartedAt);
  const session = useCallStore((s) => s.session);
  const callerNumber = useCallStore((s) => s.callerNumber);
  const sipCampaignId = useCallStore((s) => s.sipCampaignId);
  const sipCallGroupId = useCallStore((s) => s.sipCallGroupId);
  const sipLeadData = useCallStore((s) => s.sipLeadData);
  const callerContact = useCallStore((s) => s.callerContact);
  const setCallerContact = useCallStore((s) => s.setCallerContact);
  const activeCliId = useCallStore((s) => s.activeCliId);
  const setActiveCli = useCallStore((s) => s.setActiveCli);
  const loadActiveCliForAgent = useCallStore((s) => s.loadActiveCliForAgent);
  const loadActiveCountryForAgent = useCallStore((s) => s.loadActiveCountryForAgent);
  const callState = useCallStore((s) => s.callState);
  const localHangupRequested = useCallStore((s) => s.localHangupRequested);
  const outgoingRingingActive = useCallStore((s) => s.outgoingRingingActive);
  const customerRingingActive = useCallStore((s) => s.customerRingingActive);
  const declineIntent = useCallStore((s) => s.declineIntent);
  const activeCallMeta = useCallStore((s) => s.activeCallMeta);
  const completedCallToPersist = useCallStore((s) => s.completedCallToPersist);

  const isOutboundEnabled = useMemo(() => {
    const dashboardValue = agentCallsData?.currentAgent?.is_outbound_enabled;
    if (typeof dashboardValue === 'boolean') return dashboardValue;
    return userData?.agentData?.is_outbound_enabled === true;
  }, [agentCallsData, userData]);

  // demo-1.ts binds its own raw click listener to the Call button (outside
  // React's disabled/onClick), so it can't read this prop directly — mirror
  // it into the store, which it can read via useCallStore.getState().
  // Also folds in break status (see the same check on the Dialer's own
  // isOutboundEnabled prop below) so the SIP-level gate agrees with the
  // Dialer UI's — an agent on break shouldn't be able to place a call via
  // either path.
  useEffect(() => {
    useCallStore.getState().setOutboundEnabled(isOutboundEnabled && userStatus !== 'BREAK');
  }, [isOutboundEnabled, userStatus]);

  const { screen, ringtoneRef, ringtoneSrc } = useScreenState({
    callState, manualScreen, outgoingRingingActive, customerRingingActive, declineIntent, callerNumber,
  });

  const callActive = ['oncall', 'connected', 'hold', 'conference'].includes(callState);
  const dispositionActive = useCallStore((s) => s.dispositionActive);
  const wrapUpHoldForDial = useCallStore((s) => s.wrapUpHoldUntil);
  const isOnBreak = useCallStore((s) => s.isOnBreak);
  const isPlacingCall = useCallStore((s) => s.isPlacingCall);
  const webrtcPhoneStatus = useCallStore((s) => s.webrtcPhoneStatus);
  const dispositionOpen = useCallStore((s) => s.dispositionOpen);
  // Default to the dashboard (Performance section) on login, per request —
  // agents land on their own stats first instead of the Live-call view.
  const [viewMode, setViewMode] = useState('dashboard');
  const showDashboard = viewMode === 'dashboard';

  // Landing on My-dashboard by default (above) means a dial from an agent
  // sitting on Performance/Leads/etc. would otherwise never bring the
  // Live-call screen forward on its own — this switches back to it the
  // moment a call actually starts, same as before My-dashboard became the
  // default view. Watches `callState` rather than the derived `screen`:
  // clicking Call flips callState to 'dialing' immediately, but `screen`
  // itself (useScreenState.ts) only leaves 'idle' once outgoingRingingActive
  // turns true a beat later — waiting on `screen` felt laggy ("jese hi dial
  // Call button pr click kru, jese hi live call pr aa jana chahiye").
  const prevCallStateRef = useRef('idle');
  useEffect(() => {
    if (prevCallStateRef.current === 'idle' && callState !== 'idle') {
      setViewMode('live');
      // Otherwise an incoming call while the agent is on the My Account
      // page never reaches the Live-call screen at all — that branch is
      // gated on `accountOpen` first, ahead of `viewMode` (see the render
      // below), so switching viewMode alone wasn't enough.
      setAccountOpen(false);
    }
    prevCallStateRef.current = callState;
  }, [callState]);
  const isTeamLead = isTeamLeadUser(userData);
  const [accountOpen, setAccountOpen] = useState(false);
  const [dashboardDrawerOpen, setDashboardDrawerOpen] = useState(false);
  const dashboardDrawer = DASHBOARD_DRAWER;

  // ── Bootstrap: user data, role, SIP bootstrap, breaks ─────────────────
  useEffect(() => {
    const run = async () => {
      restoreActiveProfileFromSession();
      const rawUser = Storage.getItem('user');
      if (!rawUser) return;
      let user;
      try { user = JSON.parse(rawUser); } catch { return; }
      setUserData(user);
      const telephonyProfile = user?.agentData?.agentTelephony?.profile;
      if (telephonyProfile) {
        setTelephonyProfileActive(
          telephonyProfile.some((p) => (p.type === 'Soft Phone' || p.type === 'WEBRTC') && p.active_profile === true),
        );
      }
      const id = user?.agentData?.id;
      if (id) {
        setLoggedUserId(id);
        loadActiveCountryForAgent(id, user?.agentData?.agentCountryCodeDirections || []);
        fetchCampaigns(id);
        // agent-calls: first fetch comes from the loggedUserId effect below.
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!telephonyProfileActive) return undefined;
    const timeout = setTimeout(() => {
      import('@/features/cti/softphone/demo-1').catch((err) => console.error('Error loading softphone module:', err));
    }, 1000);
    return () => clearTimeout(timeout);
  }, [telephonyProfileActive]);

  useEffect(() => { useCallStore.getState().setOnBreak(userStatus === 'BREAK'); }, [userStatus]);

  const fetchRemarksStatusList = useCallback(async () => {
    try {
      const res: any = await getApiCall(API_REMARK_STATUS_LIST, {});
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setRemarkStatusList(list.filter((item) => item.status === 'Active' && !item.is_deleted));
    } catch (error) { console.log('Error fetching remarks status list', error); }
  }, []);
  useEffect(() => { fetchRemarksStatusList(); }, [fetchRemarksStatusList]);

  // Recent Calls/Call Log show "Unknown" for every record whenever the
  // backend's own name resolution (call.name/caller_profile) comes back
  // empty — which is most of the time — even when the number matches one
  // of the agent's own saved contacts. Cross-referencing client-side
  // against the same contacts list ContactsPane already fetches
  // independently (duplicated here rather than shared/lifted, matching
  // this app's existing convention for small per-surface fetches).
  const [contactNameByNumber, setContactNameByNumber] = useState<Record<string, string>>({});
  useEffect(() => {
    let mounted = true;
    getApiCall(API_ALL_CONTACTS_LIST, { fields: 'min' }) // id/name/primary_number only
      .then((resp: any) => {
        if (!mounted || !Array.isArray(resp)) return;
        const map: Record<string, string> = {};
        resp.forEach((c: any) => {
          const num = String(c?.primary_number || c?.contact_number || c?.number || '').replace(/\D/g, '').slice(-10);
          const name = `${c?.first_name || ''} ${c?.last_name || ''}`.trim() || c?.contact_name || c?.name || '';
          if (num && name) map[num] = name;
        });
        setContactNameByNumber(map);
      })
      .catch(() => { if (mounted) setContactNameByNumber({}); });
    return () => { mounted = false; };
  }, []);

  const fetchAnnouncements = useCallback(async () => {
    setAnnouncementsLoading(true);
    try {
      const res: any = await getApiCall(API_AGENT_ANNOUNCEMENT_LIST, {}, false);
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setAnnouncements(list);
    } catch (error) {
      console.log('Error fetching announcements', error);
    } finally {
      setAnnouncementsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
    const interval = setInterval(fetchAnnouncements, 300000);
    const onRefreshEvent = () => fetchAnnouncements();
    window.addEventListener('cti:announcement_refresh', onRefreshEvent);
    return () => {
      clearInterval(interval);
      window.removeEventListener('cti:announcement_refresh', onRefreshEvent);
    };
  }, [fetchAnnouncements]);

  const fetchNotifications = useCallback(async () => {
    setNotificationsLoading(true);
    try {
      const res: any = await fetchAssignedNotifications();
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setNotifications(list.filter((n: any) => !n?.is_deleted));
    } catch (error) {
      console.log('Error fetching notifications', error);
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    return watchNotifications(() => fetchNotifications());
  }, [fetchNotifications]);

  const unreadNotificationsCount = useMemo(
    () => notifications.filter((n) => !n?.is_read).length,
    [notifications],
  );

  const prevUnreadNotificationsRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevUnreadNotificationsRef.current;
    prevUnreadNotificationsRef.current = unreadNotificationsCount;
    if (prev !== null && unreadNotificationsCount > prev) playNotificationTone();
  }, [unreadNotificationsCount]);

  const markNotificationRead = useCallback(async (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    try {
      await postApiCall(API_NOTIFICATION_MARK_READ, { notification_ids: [String(id)] });
    } catch (error) {
      console.log('Failed to mark notification as read', error);
    }
  }, []);

  const markAllNotificationsRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    try {
      await postApiCall(API_NOTIFICATION_MARK_ALL_READ, {});
    } catch (error) {
      console.log('Failed to mark all notifications as read', error);
    }
  }, []);

  async function fetchCampaigns(agentId, fresh = false) {
    try {
      const base = agentId ? `${API_AGENT_DASHBOARD_CAMPAIGNS}?agent_id=${agentId}` : API_AGENT_DASHBOARD_CAMPAIGNS;
      const url = fresh ? `${base}${base.includes('?') ? '&' : '?'}fresh=1` : base;
      const res: any = await getApiCall(url, {}, false);
      setCampaigns(Array.isArray(res) ? res : []);
    } catch (error) { console.log(error); }
  }

  const agentCallsFetchedAtRef = useRef(0);
  const agentCallsSeqRef = useRef(0);
  async function fetchAgentCallsData(agentId) {
    agentCallsFetchedAtRef.current = Date.now();
    const seq = ++agentCallsSeqRef.current;
    try {
      const url = API_AGENT_DASHBOARD_CALLS.replace(':id', agentId);
      const res: any = await getApiCall(url, {}, false);
      if (seq !== agentCallsSeqRef.current) return; // a newer fetch owns the state
      const seen = new Set();
      const byKey = Array.isArray(res?.callRecords)
        ? res.callRecords.filter((r) => {
          // Dedupe by call_sid first, falling back to id.
          const key = r?.call_sid ?? r?.id;
          if (key == null || seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        : res?.callRecords;
      const callRecords = Array.isArray(byKey) ? mergeSameCallRows(byKey) : byKey;
      setAgentCallsData({ ...res, callRecords });
    } catch (error) { console.log(error); }
  }

  useDashboardRefresh('campaigns', () => (loggedUserId ? fetchCampaigns(loggedUserId, true) : undefined));
  useDashboardRefresh('leads', () => useCallStore.getState().bumpLeadsRefresh());
  useDashboardRefresh('follow_ups', () => useCallStore.getState().bumpFollowUpsRefresh());

  useEffect(() => {
    if (!loggedUserId) return undefined;
    fetchAgentCallsData(loggedUserId);
    const onVisible = () => {
      if (!document.hidden && Date.now() - agentCallsFetchedAtRef.current >= AGENT_CALLS_STALE_MS) {
        fetchAgentCallsData(loggedUserId);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loggedUserId]);

  const callLogRefreshToken = useCallStore((s) => s.callLogRefreshToken);
  const initialCallLogTokenRef = useRef(callLogRefreshToken);
  useEffect(() => {
    if (!loggedUserId || callLogRefreshToken === initialCallLogTokenRef.current) return undefined;
    const timers = AFTER_CALL_REFETCH_MS.map((ms) => setTimeout(() => fetchAgentCallsData(loggedUserId), ms));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callLogRefreshToken]);

  // Assigned CLIs. `?combined=1` returns { combined: true, source, clis }: every agent
  // number + every number of the agent's call groups (source AGENT_GROUP, each tagged with
  // cli_group_label), or — when the agent and its groups have none — the call-centre
  // default / any number as before. An older backend ignores the flag and returns the
  // agent's own list, which then runs the old chain (same as CTIAgentUI).
  useEffect(() => {
    if (!loggedUserId) { setAssignedClis([]); setCliSource(null); return; }
    const run = async () => {
      setLoadingAssignedClis(true);
      try {
        const resp: any = await getApiCall(`${API_AGENT_MY_ASSIGNED_CLIS}?combined=1`, {}, false);
        if (resp?.combined === true) {
          const combinedClis = filterOutboundClis(Array.isArray(resp?.clis) ? resp.clis : []);
          setAssignedClis(combinedClis);
          setCliSource(resp?.source || (combinedClis.length ? null : 'NONE'));
          loadActiveCliForAgent(loggedUserId, combinedClis);
          return;
        }
        const clis = filterOutboundClis(resp);
        if (clis.length > 0) {
          setAssignedClis(clis); setCliSource('AGENT'); loadActiveCliForAgent(loggedUserId, clis); return;
        }
        try {
          const fallback: any = await getApiCall(API_AGENT_CLI_FALLBACK.replace(':agentId', loggedUserId), {}, false);
          const fallbackClis = filterOutboundClis(Array.isArray(fallback?.clis) ? fallback.clis : []);
          setAssignedClis(fallbackClis);
          setCliSource(fallback?.source || (fallbackClis.length ? null : 'NONE'));
          loadActiveCliForAgent(loggedUserId, fallbackClis);
        } catch { setAssignedClis([]); setCliSource('NONE'); }
      } catch { setAssignedClis([]); setCliSource(null); }
      finally { setLoadingAssignedClis(false); }
    };
    run();
  }, [loggedUserId, loadActiveCliForAgent]);

  const handleActiveCliChange = useCallback((cliId) => {
    const selected = assignedClis.find((c) => Number(c.id) === Number(cliId)) || null;
    if (selected) {
      setActiveCli(selected, loggedUserId);
      trackActivity('cli_select', { cli_id: selected.id, number: selected.actual_number || selected.number || null });
    }
  }, [assignedClis, loggedUserId, setActiveCli]);

  const normalizeSipHeaders = useCallback((req) => {
    if (!req) return {};
    const raw = req.headers || {};
    const normalized: any = {};
    Object.keys(raw).forEach((key) => {
      const lower = String(key || '').toLowerCase();
      let value: any = null;
      try { value = req.getHeader(key); } catch { value = null; }
      if (!value) { const arr = raw[key]; if (Array.isArray(arr) && arr[0]?.raw) value = String(arr[0].raw); }
      if (typeof value === 'string') value = value.trim();
      if (value) normalized[lower] = value;
    });
    if (!normalized['call-id']) {
      try {
        const callId = req.getHeader ? req.getHeader('Call-ID') : null;
        if (callId) normalized['call-id'] = String(callId).trim();
      } catch (e) { }
    }
    return normalized;
  }, []);
  const getHeaderValue = useCallback((headers, names) => {
    if (!headers) return null;
    for (const name of Array.isArray(names) ? names : [names]) {
      const key = String(name || '').toLowerCase();
      const val = headers[key];
      if (val !== undefined && val !== null && String(val).trim() !== '') {
        const trimmed = String(val).trim();
        if (['null', 'undefined'].includes(trimmed.toLowerCase())) continue;
        return trimmed;
      }
    }
    return null;
  }, []);
  const tryParseJson = (value) => {
    if (!value || typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
    try { return JSON.parse(trimmed); } catch { return null; }
  };

  useEffect(() => {
    if (!session?.request) return;
    const headers = normalizeSipHeaders(session.request);
    const campaignIdFromHeader = getHeaderValue(headers, ['X-NS-CampaignId', 'campaign_id', 'x-campaign-id', 'x-campaign_id', 'campaign-id']);
    const callGroupIdFromHeader = getHeaderValue(headers, ['X-NS-CallGroupId']);
    const channelIdFromHeader = getHeaderValue(headers, ['X-NS-ChannelId']);
    const agentChannelIdFromHeader = getHeaderValue(headers, ['X-NS-AgentChannelId']);
    const leadJson = getHeaderValue(headers, ['x-lead-data', 'x-lead', 'lead-data', 'caller_profile']);
    const parsedLeadJson: any = tryParseJson(leadJson);
    const leadData = {
      first_name: parsedLeadJson?.first_name || getHeaderValue(headers, ['first_name', 'x-first-name', 'x-first_name']) || null,
      last_name: parsedLeadJson?.last_name || getHeaderValue(headers, ['last_name', 'x-last-name', 'x-last_name']) || null,
      company: parsedLeadJson?.company || getHeaderValue(headers, ['company', 'x-company', 'x-company-name', 'x-company_name']) || null,
      contact_number: callerNumber || null,
    };
    useCallStore.getState().setSipData({
      sipHeaders: headers,
      sipCampaignId: campaignIdFromHeader || null,
      sipCallGroupId: callGroupIdFromHeader || null,
      sipLeadData: leadData,
      channelId: channelIdFromHeader || null,
      agentChannelId: agentChannelIdFromHeader || null,
    });
    const profileName = [leadData.first_name, leadData.last_name].filter(Boolean).join(' ').trim();
    if (profileName || leadData.company) {
      useCallStore.getState().updateActiveCallMeta({
        ...(profileName ? { caller_profile: profileName } : {}),
        ...(leadData.company ? { company: leadData.company } : {}),
      });
    }
  }, [callerNumber, getHeaderValue, normalizeSipHeaders, session]);

  useEffect(() => { if (sipCampaignId) lastSipCampaignIdRef.current = sipCampaignId; }, [sipCampaignId]);
  useEffect(() => { if (sipCallGroupId) lastSipCallGroupIdRef.current = sipCallGroupId; }, [sipCallGroupId]);
  useEffect(() => { if (sipLeadData) lastSipLeadDataRef.current = sipLeadData; }, [sipLeadData]);
  useEffect(() => { if (callerContact) lastCallerContactRef.current = callerContact; }, [callerContact]);
  // New call: drop the previous call's contact/lead so the disposition
  // prefill (which runs at ringing, before callerContact hydrates) doesn't
  // reuse the last caller's name. activeLeadContext is only cleared on a
  // successful disposition submit, so a declined/failed lead call left it
  // behind and its contact_name won over the real contact on the next
  // manual dial — keep it only when this call is to that lead's number.
  // Must stay above useDispositionFlow so it runs first in the same commit.
  useEffect(() => {
    if (callState !== 'incoming' && callState !== 'dialing') return;
    lastCallerContactRef.current = null;
    lastSipLeadDataRef.current = null;
    const store = useCallStore.getState();
    const lead = store.activeLeadContext;
    if (!lead) return;
    const lastTenDigits = (v) => String(v || '').replace(/\D/g, '').slice(-10);
    const dialed = lastTenDigits(dialNumRef.current);
    if (callState === 'incoming' || !dialed || dialed !== lastTenDigits(lead.contact_number)) {
      store.clearActiveLeadContext();
    }
  }, [callState]);

  useEffect(() => {
    const campaignId = sipCampaignId;
    if (!campaignId) { setActiveCampaignFromSip(null); return; }
    let mounted = true;
    setActiveCampaignLoading(true);
    getApiCall(API_VOICE_CAMPAIGNS_GET_ONE.replace(':id', campaignId), {}, false)
      .then((resp) => {
        if (!mounted) return;
        setActiveCampaignFromSip(resp || null);
        const campaignName = resp?.campaign_name || resp?.name || resp?.data?.campaign_name || resp?.data?.name || null;
        if (campaignName) useCallStore.getState().updateActiveCallMeta({ campaign_name: campaignName });
        const camp = resp?.data || resp;
        if (String(camp?.campaign_type) === '2' && String(camp?.first_call || '').toLowerCase() === 'agent') {
          useCallStore.getState().updateActiveCallMeta({ agent_first: true });
        }
      })
      .catch(() => { if (mounted) setActiveCampaignFromSip(null); })
      .finally(() => { if (mounted) setActiveCampaignLoading(false); });
    return () => { mounted = false; };
  }, [sipCampaignId]);

  useEffect(() => {
    const groupId = sipCallGroupId;
    if (!groupId) { setActiveAgentGroupFromSip(null); return; }
    let mounted = true;
    setActiveAgentGroupLoading(true);
    getApiCall(API_SINGLE_AGENT_GROUP.replace(':id', groupId), {}, false)
      .then((resp: any) => { if (mounted) setActiveAgentGroupFromSip(resp?.data || resp || null); })
      .catch(() => { if (mounted) setActiveAgentGroupFromSip(null); })
      .finally(() => { if (mounted) setActiveAgentGroupLoading(false); });
    return () => { mounted = false; };
  }, [sipCallGroupId]);

  const activeSnoopContext = useCallStore((s) => s.activeSnoopContext);

  const ownTranscriptionEnabled = useMemo(
    () =>
      !!(
        userData?.agentData?.is_transcription_enabled ??
        userData?.agent_details?.is_transcription_enabled
      ),
    [userData],
  );
  const liveCallTranscriptionEnabled = activeSnoopContext
    ? !!activeSnoopContext.isTranscriptionEnabled
    : ownTranscriptionEnabled;

  useEffect(() => {
    const active = ['incoming', 'oncall', 'connected', 'hold', 'conference', 'dialing'].includes(callState);
    // Clear loading here too. A call-state change (incoming → oncall, hold,
    // StrictMode remount) used to cancel the in-flight request and then
    // bail out on the same fetch key, so "Loading script…" never cleared
    // and the empty state never rendered.
    if (!active || activeSnoopContext || isTeamLead) {
      setContextScript(null);
      setContextScriptSource(null);
      setContextScriptLoading(false);
      contextScriptFetchedKeyRef.current = null;
      return;
    }
    const effectiveCampaignId = sipCampaignId || lastSipCampaignIdRef.current || null;
    const effectiveCallGroupId = sipCallGroupId || lastSipCallGroupIdRef.current || null;
    const fetchKey = `${effectiveCampaignId || ''}:${effectiveCallGroupId || ''}`;
    if (contextScriptFetchedKeyRef.current === fetchKey) return;
    let cancelled = false;
    setContextScriptLoading(true);
    const params: any = {};
    if (effectiveCampaignId) params.campaign_id = effectiveCampaignId;
    if (effectiveCallGroupId) params.call_group_id = effectiveCallGroupId;
    getApiCall(API_CALL_SCRIPT_BY_CONTEXT, params, false)
      .then((resp: any) => {
        if (cancelled) return;
        // Mark fetched only after a response lands, including script: null.
        // Recording the key up front made the next run skip the request
        // while the cancelled one refused to clear loading.
        contextScriptFetchedKeyRef.current = fetchKey;
        setContextScript(resp?.script || null);
        setContextScriptSource(resp?.source || null);
      })
      .catch(() => {
        if (cancelled) return;
        setContextScript(null);
        setContextScriptSource(null);
      })
      .finally(() => { if (!cancelled) setContextScriptLoading(false); });
    return () => { cancelled = true; };
  }, [callState, sipCampaignId, sipCallGroupId, activeSnoopContext, isTeamLead]);

  const { idleSeconds, sendAgentIdleStats } = useIdleTracking({ screen, callState, loggedUserId });

  // Must come before any effect below that reads muteOn/holdOn (the
  // session-mute effect right after) — these are destructured `const`s,
  // not hoisted declarations, so an effect referencing them from a spot
  // earlier in the component body than this call would throw "Cannot
  // access 'muteOn' before initialization" (a real crash this caused once
  // already, moving handler definitions around during the hook-split).
  const {
    holdOn, muteOn, holdLoading, muteLoading, pickerMode, setPickerMode,
    handleToggleHold, handleToggleMute, handleOpenTransfer, handleOpenConference,
    handleAcceptCall, handleRejectCall, handleEndCall,
    handleSendToVoicemail, voicemailSending,
  } = useCallActions({ callState, userData, setManualScreen, isOutboundEnabled });

  useEffect(() => {
    if (session) setLocalAudioEnabled(session, !muteOn);
  }, [session, muteOn]);

  useEffect(() => {
    useTranscriptStore.getState().setAgentMuted(muteOn);
  }, [muteOn]);

  // Lock the dialer display to the caller's number while a call is live,
  // same as CTIAgentUI.tsx.
  useEffect(() => {
    if (callActive && callerNumber) {
      if (!dialerLockedPrefixRef.current) {
        dialerPrevValueRef.current = dialNumRef.current || '';
        dialerLockedPrefixRef.current = callerNumber;
        setDialNum(callerNumber);
      } else if (dialerLockedPrefixRef.current !== callerNumber) {
        const oldPrefix = dialerLockedPrefixRef.current;
        dialerLockedPrefixRef.current = callerNumber;
        setDialNum((current) => callerNumber + (current.startsWith(oldPrefix) ? current.slice(oldPrefix.length) : ''));
      }
      return;
    }
    if (callState === 'idle' && dialerLockedPrefixRef.current) {
      dialerLockedPrefixRef.current = '';
      setDialNum(dialerPrevValueRef.current || '');
      dialerPrevValueRef.current = '';
    }
  }, [callActive, callState, callerNumber]);

  const handleCallLead = useCallback((lead, group, opts?: { recall?: boolean }) => {
    if (!lead) return;
    if (!isOutboundEnabled) { toast.error('Outbound calling is disabled for this agent.'); return; }
    const { dispositionActive: dispoActive, dispositionOpen: dispoOpen, isOnBreak: onBreak, callState: cs, isPlacingCall: placing } = useCallStore.getState();
    if (dispoActive || dispoOpen) { toast.error('Finish your disposition before making another call.'); return; }
    if (isWrapUpHoldActive()) { toast.error(WRAP_UP_HOLD_DIAL_MESSAGE); return; }
    if (opts?.recall) {
      if (onBreak) { toast.error('End your break before calling.'); return; }
      if ((cs && cs !== 'idle') || placing) { toast.error('End your current call before calling.'); return; }
    }
    const number = String(lead.contact_number || '').replace(/[^\d+]/g, '');
    if (!number) { toast.error('This lead has no number to call.'); return; }
    useCallStore.getState().setActiveLeadContext({
      item_id: lead.id, assignment_id: group?.assignment_id || null, disposition_form: group?.disposition_form || null,
      contact_number: number, contact_name: lead.contact_name || null,
      is_recall: Boolean(opts?.recall),
    });
    setManualScreen(null);
    setDialNum(number);
    setTimeout(() => document.getElementById('callButtonOnIdleScreen')?.click(), 250);
  }, [isOutboundEnabled]);

  const isDialerReady = useMemo(() => {
    let profile = null;
    try { profile = sessionStorage.getItem('activeLoginType'); } catch { profile = null; }
    if (String(profile || '').toUpperCase() === 'MOBILE') return true;
    return String(webrtcPhoneStatus || '').toLowerCase() === 'connected';
  }, [webrtcPhoneStatus]);

  // Client-side gate for follow-up and call-log calls; the backend re-checks from the DB.
  const followUpCallBlockedReason = useMemo(() => {
    if (!isOutboundEnabled) return "Outbound calling isn't enabled for your profile.";
    if (isOnBreak) return "You're on a break. Please end your break before placing a call.";
    if (callState !== 'idle' || isPlacingCall) return 'Please end your current call before placing a new one.';
    if (dispositionActive || dispositionOpen) return 'Please submit your disposition before placing a call.';
    if (wrapUpHoldForDial) return WRAP_UP_HOLD_DIAL_MESSAGE;
    if (!isDialerReady) return 'Your dialer is not connected. Please connect it before placing a call.';
    return null;
  }, [
    isOutboundEnabled,
    isOnBreak,
    callState,
    isPlacingCall,
    dispositionActive,
    dispositionOpen,
    wrapUpHoldForDial,
    isDialerReady,
  ]);

  // Asks the backend for the real number (it DB-checks login / break / call /
  // disposition before releasing it), then dials it like a lead call.
  const dialReleasedNumber = useCallback(async (url, failMessage, beforeDial = null) => {
    if (followUpCallBlockedReason) { toast.error(followUpCallBlockedReason); return; }
    let number = '';
    try {
      const resp = await getApiCall(url);
      number = String(resp?.dial_number || '').replace(/[^\d+]/g, '');
    } catch (error) {
      console.error(failMessage, error);
      toast.error(friendlyApiError(error) || failMessage);
      return;
    }
    if (!number) { toast.error('There is no number to call for this entry.'); return; }
    beforeDial?.(number);
    setManualScreen(null);
    setDialNum(number);
    setTimeout(() => document.getElementById('callButtonOnIdleScreen')?.click(), 250);
  }, [followUpCallBlockedReason]);

  const handleCallFollowUp = useCallback((followUp) => {
    if (!followUp?.id) return;
    dialReleasedNumber(
      API_DISPOSITION_FOLLOW_UP_DIAL_NUMBER.replace(':id', String(followUp.id)),
      'Unable to call this follow-up.',
      (number) => useCallStore.getState().setActiveFollowUpContext({ id: followUp.id, contact_number: number }),
    );
  }, [dialReleasedNumber]);

  const handleCallFromLog = useCallback((call) => {
    if (!call?.id) return;
    dialReleasedNumber(
      API_AGENT_CALL_LOG_DIAL_NUMBER.replace(':callId', String(call.id)),
      'Unable to call this number.',
    );
  }, [dialReleasedNumber]);

  // Contacts pane's dedicated call icon — unlike onCallNumber (which just
  // fills the dial input, used for History's redial/row-click), this fills
  // the number AND dials it, same pattern as calling a lead.
  const handleQuickCallNumber = useCallback((num) => {
    if (!num) return;
    // Quick-call is an auto-dial action (fills the input then clicks Call) —
    // never allowed mid-call, since the input is locked to the live
    // caller's number while callActive and this would try to place a
    // second call over the current one.
    if (callActive) { toast.error('End the current call before dialing another number.'); return; }
    if (dispositionActive || dispositionOpen) { toast.error('Finish your disposition before making another call.'); return; }
    if (isWrapUpHoldActive()) { toast.error(WRAP_UP_HOLD_DIAL_MESSAGE); return; }
    if (!isOutboundEnabled) { toast.error('Outbound calling is disabled for this agent.'); return; }
    setDialNum(sanitizeDialInput(num));
    setTimeout(() => document.getElementById('callButtonOnIdleScreen')?.click(), 250);
  }, [isOutboundEnabled, callActive, dispositionActive, dispositionOpen]);

  const {
    dispositionSubmitting, dispositionValues, setDispositionValues, dispositionMeta,
    dispositionErrors, setDispositionErrors,
    dispositionSecondsLeft, pendingDispositions,
    submitDisposition, submitPendingDisposition,
    lastSubmittedDisposition,
    editingSubmitted, startEditSubmitted, cancelEditSubmitted, saveEditSubmitted,
    wrapUpHoldUntil, endWrapUpHold,
  } = useDispositionFlow({
    userData, loggedUserId, callState, outgoingRingingActive,
    sipCampaignId, sipCallGroupId, sipLeadData, activeCampaignFromSip, callerNumber,
    completedCallToPersist, manualScreen, setManualScreen, screen,
    lastSipCampaignIdRef, lastSipCallGroupIdRef, lastCallerContactRef, lastSipLeadDataRef,
  });

  // "Save Dispose & Next Lead" — submit like normal, then (only for a lead
  // call) fetch the next pending lead still left in that same assignment
  // group and auto-dial it, same as clicking Call on it from the Leads tab
  // would. Falls through to a no-op past the toast if there's nothing left
  // to advance to, or if the call being disposed wasn't a lead call at all.
  const queueNextLeadCall = useCallback((next, { assignmentId, excludeItemId, resumed = false }) => {
    const name = String(next?.lead?.contact_name || '').trim() || next?.lead?.contact_number || 'the next lead';
    toast.info(`Calling ${name} in ${AUTO_DIAL_NEXT_LEAD_DELAY_MS / 1000} seconds — the next lead in this list.`);
    saveNextLeadDialIntent({ agentId: userData?.agentData?.id || null, assignmentId, excludeItemId });
    clearInterval(autoDialNextLeadTimerRef.current);
    const leadId = next?.lead?.id ?? null;
    const deadline = Date.now() + AUTO_DIAL_NEXT_LEAD_DELAY_MS;
    const store = useCallStore.getState();
    store.setAutoDialNextLead({ leadId, secondsLeft: Math.ceil(AUTO_DIAL_NEXT_LEAD_DELAY_MS / 1000) });
    const tick = () => {
      const remainingMs = deadline - Date.now();
      if (remainingMs > 0) {
        useCallStore.getState().setAutoDialNextLead({ leadId, secondsLeft: Math.ceil(remainingMs / 1000) });
        return;
      }
      clearInterval(autoDialNextLeadTimerRef.current);
      clearNextLeadDialIntent();
      const s = useCallStore.getState();
      s.setAutoDialNextLead(null);
      if (s.callState !== 'idle' || s.dispositionActive || s.dispositionOpen || isWrapUpHoldActive(s)) return;
      if (String(userStatusRef.current || '').toUpperCase() === 'BREAK') return;
      trackActivity('lead_auto_dial', {
        item_id: next?.lead?.id ?? null,
        assignment_id: assignmentId ?? null,
        after_item_id: excludeItemId ?? null,
        resumed,
      });
      handleCallLead(next.lead, next.group);
    };
    autoDialNextLeadTimerRef.current = setInterval(tick, 250);
  }, [handleCallLead, userData]);
  useEffect(() => () => {
    clearInterval(autoDialNextLeadTimerRef.current);
    useCallStore.getState().setAutoDialNextLead(null);
  }, []);

  useEffect(() => {
    const agentId = userData?.agentData?.id || null;
    if (!agentId || nextLeadIntentCheckedRef.current) return;
    nextLeadIntentCheckedRef.current = true;
    const intent = takeNextLeadDialIntent(agentId);
    if (!intent) return;
    const waitUntil = Date.now() + AUTO_DIAL_RESUME_PHONE_WAIT_MS;
    let timer: any = null;
    const tryDial = async () => {
      const store = useCallStore.getState();
      const phoneReady = String(store.webrtcPhoneStatus || '').toLowerCase() === 'connected';
      if (!phoneReady) {
        if (Date.now() < waitUntil) timer = setTimeout(tryDial, 1000);
        return;
      }
      if (store.callState !== 'idle' || store.dispositionActive || store.dispositionOpen || isWrapUpHoldActive(store)) return;
      if (String(userStatusRef.current || '').toUpperCase() === 'BREAK') return;
      const next = await fetchNextPendingLeadInGroup(intent.assignment_id, intent.exclude_item_id);
      if (!next) return;
      queueNextLeadCall(next, { assignmentId: intent.assignment_id, excludeItemId: intent.exclude_item_id, resumed: true });
    };
    timer = setTimeout(tryDial, AUTO_DIAL_RESUME_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [userData, queueNextLeadCall]);

  const handleSubmitDispositionAndNext = useCallback(async () => {
    const result = await submitDisposition();
    if (!result?.ok || !result.isLead || result.isLeadRecall || !result.leadAssignmentId) return;
    const next = await fetchNextPendingLeadInGroup(result.leadAssignmentId, result.leadItemId);
    if (next) queueNextLeadCall(next, { assignmentId: result.leadAssignmentId, excludeItemId: result.leadItemId });
    else toast.info('No more pending leads in this list.');
  }, [submitDisposition, queueNextLeadCall]);

  const handleSubmitDispositionAndExit = useCallback(() => submitDisposition({ exit: true }), [submitDisposition]);

  // Same "and next" behavior, for a PENDING row's own submit — unlike the
  // Live tab, the lead's is_lead/lead_item_id/lead_assignment_id live
  // entirely on `item.context.meta` here (a snapshot taken when the
  // disposition first opened), independent of the live activeLeadContext
  // store, so there's no need to submitDisposition returning them.
  const handleSubmitPendingDispositionAndNext = useCallback(async (item, values) => {
    const meta = item?.context?.meta;
    const ok = await submitPendingDisposition(item, values);
    if (!ok || !meta?.is_lead || meta?.is_lead_recall || !meta?.lead_assignment_id) return ok;
    const next = await fetchNextPendingLeadInGroup(meta.lead_assignment_id, meta.lead_item_id);
    if (next) queueNextLeadCall(next, { assignmentId: meta.lead_assignment_id, excludeItemId: meta.lead_item_id });
    else toast.info('No more pending leads in this list.');
    return ok;
  }, [submitPendingDisposition, queueNextLeadCall]);

  // ── Call-record persistence + lead auto-complete on hangup ────────────
  // Only for a lead call that was NEVER answered — no disposition ever
  // opens for that (useDispositionFlow.ts requires answered), so nothing
  // else would ever mark it done, and it needs a real "closed" state to
  // count as attempted. This used to run unconditionally on every hangup,
  // including an ANSWERED lead call — completing the lead with empty
  // submission_data right on hangup, ahead of (and independent of) the
  // agent's actual disposition submission, which is what "Save Dispose &
  // Next Lead" needs to see to auto-advance. With the lead already marked
  // completed here, that whole chain never ran, so auto-dial-next never
  // fired for a lead call that WAS answered — the one case a real
  // disposition (and its own "and next" flow) should exist.
  useEffect(() => {
    if (!completedCallToPersist) return;
    if (completedCallToPersist.answer_status === 'answered') return;
    const leadCtx = useCallStore.getState().activeLeadContext;
    const itemId = leadCtx?.item_id;
    if (!itemId) return;
    const dedupeKey = `${itemId}:${completedCallToPersist.call_sid || 'no-sid'}`;
    if (autoCompletedLeadRef.current === dedupeKey) return;
    autoCompletedLeadRef.current = dedupeKey;
    postApiCall(API_AGENT_LEADS_COMPLETE.replace(':itemId', itemId), { data: {} })
      .then(() => {
        trackActivity('lead_auto_complete', { item_id: itemId, call_sid: completedCallToPersist.call_sid || null, answer_status: completedCallToPersist.answer_status || null });
        useCallStore.getState().bumpLeadsRefresh();
      })
      .catch((e) => console.error('Lead auto-complete failed', e));
  }, [completedCallToPersist]);

  useEffect(() => {
    if (!completedCallToPersist) return;
    const run = async () => {
      const digitsOnly = (value) => String(value || '').replace(/\D/g, '');
      const createdBy = completedCallToPersist.created_by || userData?.id || null;
      const logDirection = String(completedCallToPersist.direction || useCallStore.getState().direction || '').toLowerCase();
      const isInbound = ['incoming', 'inbound', 'in'].includes(logDirection);
      const lastTenLog = (v) => { const d = String(v ?? '').replace(/\D/g, ''); return d ? (d.length > 10 ? d.slice(-10) : d) : null; };
      const customerNumberForLog = isInbound
        ? completedCallToPersist.customer_number || completedCallToPersist.caller_number
        : completedCallToPersist.customer_number || completedCallToPersist.callee_number || completedCallToPersist.caller_number;
      let logContact: any = useCallStore.getState().callerContact || lastCallerContactRef.current || null;
      if (!logContact && customerNumberForLog) { try { const fetched = await getContactGroupMemberByNumber(lastTenLog(customerNumberForLog)); if (fetched) logContact = fetched; } catch { } }
      const logContactName = logContact ? `${logContact.first_name || ''} ${logContact.last_name || ''}`.trim() || logContact.name || null : null;
      // Saved contact's id for calls.contact_group_member_id — only when that contact really
      // holds this call's number (a stale contact from the previous call must not leak in).
      const logContactNumbers = logContact
        ? [logContact.primary_number, logContact.alt1_number, logContact.alt2_number].map(lastTenLog).filter(Boolean)
        : [];
      const logContactId =
        logContact?.id && logContactNumbers.includes(lastTenLog(customerNumberForLog)) ? Number(logContact.id) : null;
      const numberForLog = customerNumberForLog;
      const callerNumberDigits = digitsOnly(numberForLog);
      if (!createdBy || !callerNumberDigits) { useCallStore.getState().clearCompletedCallToPersist(); return; }
      let cleanedNumber = callerNumberDigits;
      if (cleanedNumber.startsWith('91') && cleanedNumber.length > 10) cleanedNumber = cleanedNumber.slice(2);
      if (cleanedNumber.length > 10) cleanedNumber = cleanedNumber.slice(-10);

      const activeCliNumber = useCallStore.getState().activeCliNumber;
      const storeDirection = useCallStore.getState().direction;
      const leadCtxForFlags = useCallStore.getState().activeLeadContext;
      const callTypeFlags = deriveCallTypeFlags({ callType: completedCallToPersist.call_type, isIvrSource: completedCallToPersist.is_ivr_source, isAgentLead: Boolean(leadCtxForFlags?.item_id) });
      const followUpCtx = useCallStore.getState().activeFollowUpContext;
      const realSipCallId = useCallStore.getState().sipHeaders?.['call-id'] || null;

      const payload = {
        did: completedCallToPersist.did || (isInbound ? null : activeCliNumber) || null,
        direction: completedCallToPersist.direction || storeDirection || null,
        caller_number: cleanedNumber,
        answer_status: completedCallToPersist.answer_status || null,
        end_time: completedCallToPersist.end_time || null,
        patch_duration: completedCallToPersist.patch_duration ?? null,
        billing_duration: completedCallToPersist.billing_duration ?? null,
        dtmf: completedCallToPersist.dtmf || null,
        dnd: completedCallToPersist.dnd ?? null,
        call_sid: realSipCallId || completedCallToPersist.call_sid || null,
        trace_id: completedCallToPersist.trace_id || null,
        start_time: completedCallToPersist.start_time || null,
        pulses: completedCallToPersist.pulses ?? null,
        hangup_cause: completedCallToPersist.hangup_cause || null,
        trunk: completedCallToPersist.trunk || null,
        callee_number: completedCallToPersist.callee_number || null,
        bridge_time: completedCallToPersist.bridge_time || null,
        bridge_ring_duration: completedCallToPersist.bridge_ring_duration ?? null,
        recording_file: completedCallToPersist.recording_file || null,
        recording_uploaded: completedCallToPersist.recording_uploaded ?? false,
        caller_profile: completedCallToPersist.caller_profile || logContactName || null,
        contact_group_member_id: logContactId,
        category: completedCallToPersist.category || 'cti',
        agent_profile_type: String(getActiveTelephonyProfile()?.type || '').trim().toUpperCase() || null,
        is_missed: completedCallToPersist.is_missed ?? false,
        is_declined: completedCallToPersist.is_declined ?? false,
        ...callTypeFlags,
        is_follow_up: Boolean(followUpCtx?.id),
        is_recall_lead: Boolean(leadCtxForFlags?.item_id && leadCtxForFlags?.is_recall),
        is_voicemail: useCallStore.getState().voicemailSentForActiveCall === true,
        created_by: createdBy,
        agent_id: userData?.agentData?.id || null,
        mute_duration: completedCallToPersist.mute_duration ?? null,
      };
      const followUpAnswered =
        String(completedCallToPersist.answer_status || '').toLowerCase() === 'answered' ||
        Number(completedCallToPersist.billing_duration) > 0;
      postApiCall(API_CALLS_CREATE, payload)
        .then(() => {
          if (!followUpCtx?.id || !followUpAnswered || !payload.call_sid) return null;
          return patchApiCall(
            API_DISPOSITION_FOLLOW_UP_COMPLETE_BY_CALL.replace(':id', String(followUpCtx.id)),
            { call_sid: payload.call_sid },
          )
            .then((resp) => {
              // patchApiCall resolves to the response's `data` payload.
              if (resp?.completed) {
                toast.success('Follow-up marked completed');
                useCallStore.getState().bumpFollowUpsRefresh?.();
              }
            })
            .catch((error) => console.error('Failed to complete follow-up', error));
        })
        .catch((error) => console.error('Failed to save call record', error))
        .finally(() => {
          useCallStore.getState().clearCompletedCallToPersist();
          useCallStore.getState().clearActiveFollowUpContext?.();
        });
    };
    run();
  }, [completedCallToPersist, userData]);

  const maskNumbers = getAgentMasking(userData);

  const { requestNumbers, getHistory, getRemarkForCall, isHistoryPending } = useCustomerHistoryCache();

  useEffect(() => {
    // Was skipped entirely `if (maskNumbers) return` — masking only needs
    // to hide the raw digits in the UI (maskNumber() at every display
    // site), not stop this fetch; skipping the fetch meant the remarks
    // cache never had anything in it for a masked agent, so Recent Calls'
    // remark chip stayed empty regardless of DialerRecentCalls.tsx's own
    // display logic.
    const numbers = (agentCallsData?.callRecords || [])
      .map((r) => r?.dial_number || callRowCustomerNumber(r))
      .filter(Boolean);
    requestNumbers(numbers);
  }, [agentCallsData, requestNumbers]);

  // Lock page scroll while this fullscreen portal is mounted — same
  // pattern as CTIAgentUI.tsx's focusMode effect.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  // Route call audio, ringtone and hangup sound (the <audio> tags at the
  // bottom of this page) to the speaker picked in Audio settings. They
  // don't exist while tabBlocked renders its notice, hence the dep; saving
  // in the dialog re-applies on its own.
  useEffect(() => {
    if (!tabBlocked) applySpeakerToAll();
  }, [tabBlocked]);

  useEffect(() => followSpeakerChanges(), []);

  if (tabBlocked) {
    return createPortal(
      <div className="ap-shell fixed inset-0 z-[1000] flex h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md space-y-2 rounded-lg border border-border bg-card p-6 text-center">
          <h2 className="text-base font-semibold text-foreground">
            Agent portal already open
          </h2>
          <p className="text-sm text-muted-foreground">
            This account is signed in on another tab. Close this tab and continue
            there — running two portals at once would split your calls between them.
          </p>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div className="ap-shell fixed inset-0 z-[1000] flex h-screen min-h-0 flex-col gap-2 overflow-y-auto p-2 lg:flex-row lg:overflow-visible">
      <ExitGuard />
      <ReloadGuardDialog />
      <div className="flex flex-shrink-0">
        <Dialer
          userData={userData}
          theme={theme}
          dialNum={dialNum}
          setDialNum={setDialNum}
          onDialBack={() => setDialNum((current) => {
            const lockedPrefix = dialerLockedPrefixRef.current;
            if (lockedPrefix && current.length <= lockedPrefix.length) return current;
            return current.slice(0, -1);
          })}
          holdOn={holdOn}
          muteOn={muteOn}
          holdLoading={holdLoading}
          muteLoading={muteLoading}
          onToggleHold={handleToggleHold}
          onToggleMute={handleToggleMute}
          onOpenTransfer={handleOpenTransfer}
          onOpenConference={handleOpenConference}
          onAccept={handleAcceptCall}
          onReject={handleRejectCall}
          onEndCall={handleEndCall}
          onVoicemail={handleSendToVoicemail}
          voicemailSending={voicemailSending}
          assignedClis={assignedClis}
          loadingAssignedClis={loadingAssignedClis}
          cliSource={cliSource}
          activeCliId={activeCliId}
          onActiveCliChange={handleActiveCliChange}
          contactNameByNumber={contactNameByNumber}
          // Dialer renders in its own fixed column outside the
          // BreakScreen/LiveCallStack swap, so it stayed fully live during
          // a break — an agent on break could still dial. Folding break
          // status into the same isOutboundEnabled flag reuses every gate
          // already wired to it (keypad, manual input, Call button, and
          // demo-1.ts's store-synced check) instead of threading a new prop.
          isOutboundEnabled={isOutboundEnabled && userStatus !== 'BREAK'}
          agentCallsData={agentCallsData}
          screen={screen}
          idleSeconds={idleSeconds}
          dispositionSecondsLeft={dispositionSecondsLeft}
          dispositionTotalSeconds={dispositionMeta?.wrap_up_seconds ?? null}
          getRemarkForCall={getRemarkForCall}
          profileOnly={isTeamLead && callState === 'idle'}
        />
      </div>

      <div className="ap-main flex min-h-0 min-w-0 flex-none flex-col gap-2 lg:flex-1">
        <Topbar
          userData={userData}
          activeViewTab={showDashboard ? 'dashboard' : 'live'}
          onViewModeChange={(mode) => { setAccountOpen(false); setViewMode(mode); }}
          isTeamLead={isTeamLead}
          agentId={loggedUserId}
          campaigns={campaigns}
          showAgentContext={userStatus !== 'BREAK'}
          onOpenAccount={() => setAccountOpen(true)}
          showDrawerToggle={Boolean(dashboardDrawer) && showDashboard}
          drawerOpen={dashboardDrawerOpen}
          onToggleDrawer={() => setDashboardDrawerOpen((v) => !v)}
          unreadNotifications={unreadNotificationsCount}
          onToggleNotifications={() => setNotifDrawerOpen((v) => !v)}
          pendingDispositionsCount={pendingDispositions.length}
          onPendingDispositionsClick={() => { setAccountOpen(false); setViewMode('live'); }}
          onBeforeLogout={() => {
            clearNextLeadDialIntent();
            sendAgentIdleStats(new Date().toISOString());
          }}
        />

        <WrapUpHoldBanner until={wrapUpHoldUntil} onEnd={endWrapUpHold} />

        <div className="flex min-h-0 min-w-0 flex-none flex-col gap-2 lg:flex-1 lg:flex-row">
          {accountOpen ? (
            <div className="cti-app-shell flex h-[85vh] min-h-0 min-w-0 flex-1 flex-col overflow-hidden lg:h-full" data-theme={theme}>
              <UserProfilePage onBack={() => setAccountOpen(false)} />
            </div>
          ) : (
            <>
              {/* Dashboard branch — stays mounted (CSS-hidden, not
               * unmounted) even while viewing Live call, so its portaled
               * header tabs (Performance/Voicemails/...) stay visible and
               * clickable from either view; clicking one switches into
               * dashboard mode itself via onRequestDashboard. */}
              <div className={`cti-app-shell h-[85vh] min-w-0 flex-1 lg:h-auto ${showDashboard ? '' : 'hidden'}`} data-theme={theme}>
                {isTeamLead ? (
                  <TeamDashboardView
                    userData={userData}
                    theme={theme}
                    isOutboundEnabled={isOutboundEnabled}
                    onCallLead={handleCallLead}
                    onRequestDashboard={() => setViewMode('dashboard')}
                  />
                ) : (
                  <AgentDashboardView
                    userData={userData}
                    theme={theme}
                    isOutboundEnabled={isOutboundEnabled}
                    onCallLead={handleCallLead}
                    onCallFollowUp={handleCallFollowUp}
                    onCallFromLog={handleCallFromLog}
                    followUpCallBlockedReason={followUpCallBlockedReason}
                    agentCallsData={agentCallsData}
                    campaigns={campaigns}
                    onRefreshCampaigns={() => (loggedUserId ? fetchCampaigns(loggedUserId, true) : undefined)}
                    onRequestDashboard={() => setViewMode('dashboard')}
                    pendingDispositionsCount={pendingDispositions.length}
                    active={showDashboard}
                  />
                )}
              </div>

              {showDashboard && dashboardDrawer ? (
                <SideDrawer
                  open={dashboardDrawerOpen}
                  onToggle={() => setDashboardDrawerOpen((v) => !v)}
                  title={dashboardDrawer.title}
                  side="right"
                >
                  {dashboardDrawer.content}
                </SideDrawer>
              ) : null}

              {/* Live-call branch — same treatment: hidden while viewing
               * the dashboard instead of unmounted. LiveCallStack's own
               * header comment already assumed it would stay mounted
               * regardless of screen state; this now actually matches
               * that instead of unmounting it on every dashboard toggle. */}
              <div className={`cti-app-shell flex h-[85vh] min-h-0 min-w-0 flex-1 flex-col lg:h-full ${showDashboard ? 'hidden' : ''}`} data-theme={theme}>

                {userStatus === 'BREAK' ? (
                  <BreakScreen
                    breakTitle={activeBreak?.title}
                    breakDescription={activeBreak?.description}
                    breakDuration={activeBreak?.duration}
                    countdown={countdown}
                    breakExceeded={breakExceeded}
                    breakEndTime={breakEndTime}
                    ending={breakActionLoading}
                    onEndBreak={async () => {
                      const user = JSON.parse(Storage.getItem('user') || '{}');
                      if (!user?.id) { stopBreak?.(user?.id); return; }
                      try { setBreakActionLoading(true); await stopBreak?.(user?.id); } finally { setBreakActionLoading(false); }
                    }}
                  />
                ) : (<LiveCallStack
                  hideScript={Boolean(activeSnoopContext) || isTeamLead}
                  transcriptionEnabled={liveCallTranscriptionEnabled}
                  contextScript={contextScript}
                  contextScriptSource={contextScriptSource}
                  contextScriptLoading={contextScriptLoading}
                  dispositionMeta={dispositionMeta || (editingSubmitted ? lastSubmittedDisposition?.meta : null)}
                  dispositionValues={dispositionValues}
                  setDispositionValues={setDispositionValues}
                  dispositionErrors={dispositionErrors}
                  setDispositionErrors={setDispositionErrors}
                  remarkStatusList={remarkStatusList}
                  dispositionSubmitting={dispositionSubmitting}
                  onSubmitDisposition={editingSubmitted ? saveEditSubmitted : submitDisposition}
                  onSubmitDispositionAndNext={handleSubmitDispositionAndNext}
                  onSubmitDispositionAndExit={handleSubmitDispositionAndExit}
                  editingSubmitted={editingSubmitted}
                  onEditSubmitted={startEditSubmitted}
                  onCancelEditSubmitted={cancelEditSubmitted}
                  customerHistory={getHistory(dispositionMeta?.callerNumber || callerNumber)}
                  userData={userData}
                  dispositionSecondsLeft={editingSubmitted ? null : dispositionSecondsLeft}
                  lastSubmittedDisposition={lastSubmittedDisposition}
                  pendingDispositions={pendingDispositions}
                  onSubmitPending={submitPendingDisposition}
                  onSubmitPendingAndNext={handleSubmitPendingDispositionAndNext}
                  maskNumbers={maskNumbers}
                />
                )}
              </div>
              <div className={`cti-app-shell flex h-[75vh] min-h-0 w-full flex-shrink-0 flex-col lg:h-full lg:w-[320px] ${showDashboard ? 'hidden' : ''}`} data-theme={theme}>
                <CTIRightRail
                  callRecords={agentCallsData?.callRecords || []}
                  customerHistory={getHistory(dispositionMeta?.callerNumber || callerNumber)}
                  customerHistoryLoading={isHistoryPending(dispositionMeta?.callerNumber || callerNumber)}
                  // Same reasoning as Dialer.tsx's Recent Calls pick — the
                  // dialer input is locked to the LIVE caller's number
                  // during an active call, so filling it with a different
                  // contact's number here (Contacts/History rows) while
                  // still on that call would silently swap what looked
                  // like the current caller.
                  onCallNumber={(num) => { if (num && !callActive) setDialNum(sanitizeDialInput(num)); }}
                  onQuickCallNumber={handleQuickCallNumber}
                  activeCustomerNumber={dispositionMeta?.callerNumber || callerNumber}
                  queueName={activeAgentGroupFromSip?.group_name || null}
                  maskNumbers={maskNumbers}
                  footer={<AgentBreakPanel />}
                  announcements={announcements}
                  announcementsLoading={announcementsLoading}
                  onRefreshAnnouncements={fetchAnnouncements}
                  contactNameByNumber={contactNameByNumber}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <NotificationDrawer
        open={notifDrawerOpen}
        onClose={() => setNotifDrawerOpen(false)}
        notifications={notifications}
        loading={notificationsLoading}
        onMarkRead={markNotificationRead}
        onMarkAllRead={markAllNotificationsRead}
      />

      <CallQualityFeedbackTray />

      <div className="cti-app-shell" data-theme={theme}>
        <TransferConferencePicker
          open={pickerMode != null}
          mode={pickerMode || 'transfer'}
          onOpenChange={(next) => !next && setPickerMode(null)}
          userData={userData}
        />
      </div>
      {/* No local disconnect2/keypadConnect2 hidden buttons here — Header.tsx
        * (part of AppShell, which still wraps this /cti2 route even though
        * this whole page renders as a fixed full-screen overlay on top of
        * it) already renders its own copy, and demo-1.ts's initDailer()
        * binds its connect/register handler to EVERY element with that
        * class name via getElementsByClassName — a second copy here meant
        * every login click fired 2 concurrent connect/register attempts
        * (4 once loginAsProfile's own clickAll ran too), which is very
        * likely why WebRTC login got stuck "connecting" roughly half the
        * time (a race between overlapping SIP UA registration attempts). */}
      <audio id="remoteAudio" autoPlay playsInline></audio>
      <audio ref={ringtoneRef} src={ringtoneSrc} />
      <audio ref={hangupAudioRef} src={hangupAudio} preload="auto" id="hangupAudio" />
    </div>,
    document.body,
  );
}
